import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import styled from "styled-components";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { colors } from "../../theme/color";
import { HTMLAttributes } from "react";

interface CustomModalProps
  extends React.PropsWithChildren,
    React.HTMLAttributes<any> {
  header?:
    | string
    | React.ReactNode
    | {
        title: string | React.ReactNode;
        subtitle: string | React.ReactNode;
      };
  open?: null | boolean;
  onClose?: () => void;
  onSubmit?: () => void;
  closeActionText?: string | React.ReactNode;
  submitActionText?: string | React.ReactNode;
  btn1Bg?: string;
  btn1Border?: string;
  btn1Color?: string;
  btn2Bg?: string;
  btn2Border?: string;
  btn2Color?: string;
  btnPadding?: string;
  btnBorderRadius?: string;
  disabled?: boolean;
}
/**
 * CustomModal
 * @param props CustomModalProps
 * @param header  null | string | React.ReactNode
 * @param open  null | boolean
 * @param onClose  ()=>void
 * @param onSubmit  ()=>void
 * @param closeActionText  string | React.ReactNode
 * @param submitActionText  string | React.ReactNode
 * @constructor
 * @description <CustomModalProps header={"Header component"} onClose={()=>{}} open={true} />
 */

export const CustomModal: React.FC<CustomModalProps> = (props) => {
  const showActions =
    props.submitActionText || props.closeActionText || props.onSubmit;
  const header = props.header as any;
  const title = (
    (header as any)?.title ? (
      <>
        <TitleTag>{header?.title}</TitleTag>
        {header?.subtitle && <SubTitleTag>{header?.subtitle}</SubTitleTag>}
      </>
    ) : (
      props?.header
    )
  ) as any;
  return (
    <React.Fragment>
      <Dialog
        open={!!props.open}
        onClose={props.onClose}
        aria-labelledby="responsive-dialog-title"
        sx={{
          zIndex: 10000,
          "& .MuiPaper-rounded": {
            borderRadius: "12px",
            overflowY: "inherit !important",
          },
        }}
        scroll={"paper"}
      >
        {props.header && (
          <ModalHeader>
            <header>{title}</header>
            <CloseIconButton onClick={props.onClose} />
          </ModalHeader>
        )}
        <DialogContent className={props.className}>
          {props.children}
        </DialogContent>
        {showActions && (
          <DialogActions>
            <Button
              autoFocus
              disabled={props.disabled}
              onClick={props.onClose}
              sx={{
                ...(props.disabled && {
                  opacity: "0.4",
                  cursor: "not-allowed",
                }),
                backgroundColor: `${props.btn1Bg}`,
                border: `${props.btn1Border}`,
                color: `${props.btn1Color}`,
                padding: `${props.btnPadding}`,
                borderRadius: `${props.btnBorderRadius}`,
              }}
            >
              {props.closeActionText || "Cancel"}
            </Button>
            {props.onSubmit && (
              <Button
                onClick={props.onSubmit}
                autoFocus
                disabled={props.disabled}
                sx={{
                  ...(props.disabled && {
                    opacity: "0.4",
                    cursor: "not-allowed",
                  }),
                  backgroundColor: `${props.btn2Bg}`,
                  border: `${props.btn2Border}`,
                  color: `${props.btn2Color}`,
                  padding: `${props.btnPadding}`,
                  borderRadius: `${props.btnBorderRadius}`,

                  "&:hover": {
                    backgroundColor: `${props.btn2Bg}`,
                  },
                }}
              >
                {props.submitActionText || "Submit"}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </React.Fragment>
  );
};
export default CustomModal;

const ModalHeader = styled.section`
  background: ${colors.PRIMARY_GREY};
  height: 111px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
`;
const CloseIconButton = styled.img.attrs({
  src: "/assets/icons/close.svg",
  alt: "Close",
})`
  cursor: pointer;
  padding: 10px;
  @media print {
    display: none;
  }
`;

const TitleTag = styled.h3`
  color: ${colors.BLACK};
  font-family: "GT Walsheim Pro";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.36px;
  margin: 0;
  padding-bottom: 5px;
`;

const SubTitleTag = styled.p`
  color: #8c8b8b;
  font-family: "GT Walsheim Pro";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.28px;
  margin: 0;
`;
